// react
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

// api
import {
  daluxfmServerOpenApi,
  type Photo,
  type Ticket,
  useSendMessagePhotoMutation,
  useUpdateTicketMutation,
  useUploadFileMutation,
} from '@/api/generated/daluxfm/daluxfm';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import DaluxTicketFields from './DaluxTicketFields';
import {
  toDaluxTicketFormValues,
  toDaluxTicketPayload,
} from './daluxTicketForm.mappers';
import {
  createDaluxTicketFormSchema,
  type DaluxBuildingOption,
  type DaluxTicketFormValues,
  type DaluxTopicOption,
} from './daluxTicketForm.schema';

// libraries
import { zodResolver } from '@hookform/resolvers/zod';
import type { ThunkDispatch } from '@reduxjs/toolkit';
import type { AnyAction } from 'redux';

type EditDaluxTicketFormProps = {
  ticket: Ticket;
  topics: DaluxTopicOption[];
  buildingOptions: DaluxBuildingOption[];
  photos?: Photo[];
  onCancel: () => void;
  onSuccess?: () => void;
};

export default function EditDaluxTicketForm({
  ticket,
  topics,
  buildingOptions,
  photos,
  onCancel,
  onSuccess,
}: EditDaluxTicketFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation('dalux-integration');
  const dispatch = useDispatch() as ThunkDispatch<unknown, unknown, AnyAction>;
  const ticketId = ticket?.data?.ticketId;

  const schema = createDaluxTicketFormSchema(t);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<DaluxTicketFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toDaluxTicketFormValues({ ticket }),
    mode: 'onTouched',
  });

  const [updateTicket, { isLoading: isSaving }] = useUpdateTicketMutation();
  const [uploadFile, { isLoading: isUploadingPhoto }] = useUploadFileMutation();
  const [sendMessagePhoto, { isLoading: isAttachingPhoto }] =
    useSendMessagePhotoMutation();

  const onSubmit = handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      if (!ticketId) {
        throw new Error('Missing ticket id');
      }

      await updateTicket({
        ticketId,
        ticket: toDaluxTicketPayload(values),
      }).unwrap();

      if (values.photo && ticketId) {
        const uploadSessionRequest = dispatch(
          daluxfmServerOpenApi.endpoints.createUploadSession.initiate(
            undefined,
            { forceRefetch: true },
          ),
        );

        try {
          const uploadSession = await uploadSessionRequest.unwrap();
          const uploadSessionId = uploadSession?.data?.sessionId;

          if (!uploadSessionId) {
            throw new Error('Missing upload session id');
          }

          await uploadFile({
            uploadSessionId,
            'X-File-Name': values.photo.name,
            body: values.photo,
          }).unwrap();

          await sendMessagePhoto({
            ticketId,
            messagePhoto: {
              data: {
                uploadSessionIds: [uploadSessionId],
                message: 'image from energyxplorer',
              },
            },
          }).unwrap();
        } catch {
          dispatch(
            showSnackbar({
              message: t('ticket.photo_upload_error'),
              severity: 'error',
            }),
          );
          return;
        } finally {
          uploadSessionRequest.unsubscribe();
        }
      }

      dispatch(
        showSnackbar({ message: t('ticket.updated'), severity: 'success' }),
      );
      onSuccess?.();
    } catch {
      dispatch(
        showSnackbar({ message: t('ticket.update_error'), severity: 'error' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="dalux-ticket-edit-form">
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Card variant="outlined">
          <CardContent>
            <DaluxTicketFields
              control={control}
              t={t}
              disabled={isSaving || isUploadingPhoto || isAttachingPhoto}
              topics={topics}
              buildingOptions={buildingOptions}
              showPhotoUpload
            />
          </CardContent>
        </Card>

        {photos && photos.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                {t('ticket.existing_photos')}
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                {photos.map((photo) => (
                  <Box
                    key={photo?.data?.photoId}
                    component="img"
                    src={photo?.data?.url || ''}
                    alt={photo?.data?.name || 'Ticket photo'}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 1,
                      objectFit: 'cover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton onClick={onCancel} disabled={isSubmitting}>
            {t('ticket.cancel')}
          </CancelButton>
          <SubmitButton
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
          >
            {t('ticket.update')}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
